import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  electronicInvoiceArtifactSchema,
  electronicInvoiceCommitSchema,
  electronicInvoiceDocumentParametersSchema,
  electronicInvoiceFormatSchema,
  electronicInvoiceImportSchema,
  electronicInvoicePrepaymentReviewSchema,
  electronicInvoiceLookupsSchema,
  electronicInvoiceOverviewSchema,
  electronicInvoiceArchiveQuerySchema,
  electronicInvoiceSettingsSchema,
  electronicInvoiceSetupSchema,
  type ElectronicInvoiceCommit,
  type ElectronicInvoiceFormat,
  type ElectronicInvoiceParameters,
  type ElectronicInvoiceSettings,
} from '@erp/shared';
import { apiFetch } from '@/lib/api-client';
import type { z } from 'zod';

const base = '/api/v1/electronic-invoices';
export const useElectronicInvoiceOverview = (input: {
  page: number;
  importsPage: number;
  q: string;
}) =>
  useQuery({
    queryKey: ['electronic-invoices', 'overview', input],
    queryFn: () => {
      const filters = electronicInvoiceArchiveQuerySchema.parse({
        page: input.page,
        importsPage: input.importsPage,
        ...(input.q.trim() ? { q: input.q.trim() } : {}),
      });
      const params = new URLSearchParams({
        page: String(filters.page),
        importsPage: String(filters.importsPage),
        pageSize: String(filters.pageSize),
        ...(filters.q ? { q: filters.q } : {}),
      });
      return apiFetch(`${base}?${params}`, electronicInvoiceOverviewSchema);
    },
  });
export const useElectronicInvoiceSettings = () =>
  useQuery({
    queryKey: ['electronic-invoices', 'settings'],
    queryFn: () => apiFetch(`${base}/settings`, electronicInvoiceSettingsSchema),
  });
export const useElectronicInvoiceSetup = (enabled = true) =>
  useQuery({
    queryKey: ['electronic-invoices', 'setup'],
    queryFn: () => apiFetch(`${base}/setup`, electronicInvoiceSetupSchema),
    enabled,
  });
export const useElectronicInvoiceLookups = (supplierId?: string) =>
  useQuery({
    queryKey: ['electronic-invoices', 'lookups', supplierId],
    queryFn: () =>
      apiFetch(
        `${base}/lookups${supplierId ? `?supplierId=${encodeURIComponent(supplierId)}` : ''}`,
        electronicInvoiceLookupsSchema,
      ),
  });
export const useElectronicInvoiceParameters = (documentId: string) =>
  useQuery({
    queryKey: ['electronic-invoices', 'parameters', documentId],
    queryFn: () =>
      apiFetch(
        `${base}/documents/${documentId}/parameters`,
        electronicInvoiceDocumentParametersSchema,
      ),
  });
export function useElectronicInvoiceMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (
      command:
        | { action: 'export'; documentId: string; format: ElectronicInvoiceFormat; key: string }
        | { action: 'import'; file: File; key: string }
        | { action: 'commit'; id: string; input: ElectronicInvoiceCommit; key: string }
        | {
            action: 'prepayments';
            id: string;
            input: z.infer<typeof electronicInvoicePrepaymentReviewSchema>;
            key: string;
          }
        | { action: 'settings'; input: ElectronicInvoiceSettings; key: string }
        | {
            action: 'parameters';
            documentId: string;
            input: { version: number; parameters: ElectronicInvoiceParameters };
            key: string;
          },
    ) => {
      const headers = { 'Idempotency-Key': command.key };
      if (command.action === 'export')
        return apiFetch(`${base}/exports`, electronicInvoiceArtifactSchema, {
          method: 'POST',
          headers,
          body: {
            documentId: command.documentId,
            format: electronicInvoiceFormatSchema.parse(command.format),
          },
        });
      if (command.action === 'import') {
        const formData = new FormData();
        formData.append('file', command.file);
        return apiFetch(`${base}/imports`, electronicInvoiceImportSchema, {
          method: 'POST',
          headers,
          formData,
        });
      }
      if (command.action === 'commit')
        return apiFetch(`${base}/imports/${command.id}/commit`, electronicInvoiceImportSchema, {
          method: 'POST',
          headers,
          body: electronicInvoiceCommitSchema.parse(command.input),
        });
      if (command.action === 'prepayments')
        return apiFetch(
          `${base}/imports/${command.id}/prepayments`,
          electronicInvoiceImportSchema,
          {
            method: 'POST',
            headers,
            body: electronicInvoicePrepaymentReviewSchema.parse(command.input),
          },
        );
      if (command.action === 'settings')
        return apiFetch(`${base}/settings`, electronicInvoiceSettingsSchema, {
          method: 'PUT',
          headers,
          body: electronicInvoiceSettingsSchema.parse(command.input),
        });
      return apiFetch(
        `${base}/documents/${command.documentId}/parameters`,
        electronicInvoiceDocumentParametersSchema,
        { method: 'PUT', headers, body: command.input },
      );
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['electronic-invoices'] });
      await client.invalidateQueries({ queryKey: ['documents'] });
      await client.invalidateQueries({ queryKey: ['procurement'] });
    },
  });
}
